import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { isStringEmpty } from '@common/helpers';
import { AirportI18nAll } from '@common/interfaces/master/airport_i18n_all';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { TripSummaryService } from '@common/services/my-booking/trip-summary.service';
import { SupportComponent } from '@lib/components/support-class';
import { DateFormatPipe, StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { Subscription } from 'rxjs';
import { GetOrderResponseData, Type1, Type1Departure } from 'src/sdk-servicing';
import { AppConstants } from '@conf/app.constants';

const langs = ['ja', 'cn', 'tw', 'hk', 'zh', 'ko'];

@Component({
  selector: 'asw-departure',
  templateUrl: './departure.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepartureComponent extends SupportComponent {
  @Input() boundIdx!: number;
  @Input() flightIdx!: number;

  flight: Type1 = {};
  departure: Type1Departure = {};

  private _subscriptions = new Subscription();
  private _airportI18nAll: Array<AirportI18nAll> = [];
  private _originDepartureDateTime: string = '';

  /** 定数用 */
  public appConstants = AppConstants;

  constructor(
    public tripSummarySvc: TripSummaryService,
    private _myBookingItineraryService: MyBookingItineraryService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _dateFormat: DateFormatPipe,
    private _staticMsg: StaticMsgPipe,
    private _common: CommonLibService,
    private _change: ChangeDetectorRef
  ) {
    super(_common);
  }

  override init(): void {
    this._subscriptions.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        const pnr = (servicingCommon.getOrder as GetOrderResponseData) ?? {};
        this._originDepartureDateTime = pnr.air?.bounds?.at(this.boundIdx)?.originDepartureDateTime ?? '';
        this.flight = pnr.air?.bounds?.at(this.boundIdx)?.flights?.at(this.flightIdx) ?? {};
        this.departure = this.flight.departure ?? {};
        this._change.markForCheck();
      })
    );
    this._subscriptions.add(
      this._myBookingItineraryService.masterData$.subscribe((result) => {
        this._airportI18nAll = result.airportI18nAll ?? [];
        this._change.markForCheck();
      })
    );
  }

  // (1.1.20) 出発日(セグメント)
  getDepartureDateTime(): string {
    /** 1)
     * 当該バウンド.originDepartureDateTimeが存在する、かつ
     * 当該セグメント情報.departure.dateTimeが存在し、
     * 当該セグメント情報.departure.dateTime ≠ 当該バウンド.originDepartureDateTimeの場合、
     * 当該セグメント情報.departure.dateTimeを表示する。
     */
    if (
      !isStringEmpty(this._originDepartureDateTime) &&
      !isStringEmpty(this.departure?.dateTime) &&
      this.departure?.dateTime !== this._originDepartureDateTime
    )
      /**
       * [日本語]: M月d日EEEE
       * [英語]: MMM d EEEE
       */
      return this._dateFormat.transform(this.departure?.dateTime ?? '', 'default_itineraryRsDate');
    /** 2)
     * 当該バウンド.originDepartureDateTimeが存在する、かつ
     * 当該セグメント情報.departure.dateTimeが存在しないの場合、
     * 未定の旨を表示を表示する。
     */
    if (!isStringEmpty(this._originDepartureDateTime) && isStringEmpty(this.departure?.dateTime))
      return this._staticMsg.transform('label.hyphen');
    // 3) 上記以外の場合、非表示とする。
    return '';
  }

  // (1.1.23) 深夜出発日時
  getLateNightFlightDateTime(): string {
    const isValidLang = langs.includes(this._common.aswContextStoreService.aswContextData.lang ?? '');
    /**
     * 当該セグメント情報.isLateNightDeparture=true(深夜便)、
     * かつユーザ共通情報.data.aswContext.lang="ja"(日本語)、または"zh"(中国語)、または"ko"(韓国語)の場合、
     * 下記の計算を元に深夜出発日時を表示する
     * =============================
     * 月日= 深夜出発日時の前日(※1)
     * HH(時)=深夜出発日時(※1)の時+24
     * mm(分)=深夜出発日時(※1)の分
     * 例：1/1 1:46 -> 12/31 25:46
     */
    if (this.flight.isLateNightDeparture && isValidLang) {
      /** ※1
       * 当該セグメント情報.departure.estimatedDateTimeが存在する場合、
       * 当該セグメント情報.departure.estimatedDateTimeとする
       * 上記以外の場合、当該セグメント情報.departure.dateTimeとする
       */
      return this._dateFormat.transform(
        this.departure?.estimatedDateTime || this.departure?.dateTime || '',
        'availability.flightplan_departuredate.datetime.lateNight',
        this.flight.isLateNightDeparture
      );
    }
    return '';
  }

  // (1.1.24) 出発地
  getDepartureLocationName(locationCode: string = '', locationName: string = ''): string {
    // 1) 当該セグメント情報.departure.locationCodeを基に「イベントID(1)」で取得した空港リストをフィルタして、空港名称を取得する。
    const airportName =
      this._airportI18nAll?.find((item: AirportI18nAll) => item?.airport_code === locationCode)?.airport_name ?? '';
    // 2) 取得できない場合は当該セグメント情報.departure.locationNameを表示する。
    return airportName || locationName;
  }

  // (1.1.26) 出発搭乗口
  get tooltipDisplayTerminal() {
    const isHanedaLocation = this.departure?.locationCode === 'HND' || this.flight.arrival?.locationCode === 'HND';
    /** 1)
     * (
     *    当該セグメント情報.departure.locationCode="HND"(haneda)または
     *    当該セグメント情報.arrival.locationCode="HND"(haneda)
     * )、かつ
     * 当該セグメント情報.isNhGroupOperated=true(NH運航)、
     * かつセグメント情報.isJapanDomesticFlight=false(国際線)
     */
    let condition1 = isHanedaLocation && this.flight?.isNhGroupOperated && !this.flight?.isJapanDomesticFlight;
    // 2) (当該セグメント情報.departure.locationCode="HND"(haneda)または当該セグメント情報.arrival.locationCode="HND"(haneda))、かつ当該セグメント情報.marketingAirlineCode="NH"、かつ当該セグメント情報.operatingAirlineCode="7G"(スターアライアンス加盟キャリア運航)、かつセグメント情報.isJapanDomesticFlight=true(国内線)
    let condition2 =
      isHanedaLocation &&
      // 提携: 予約詳細(旅程)画面 QA No.5より下記"NH", "7G"のロジックはそのままで良い
      this.flight?.marketingAirlineCode === 'NH' &&
      this.flight?.operatingAirlineCode === '7G' &&
      this.flight?.isJapanDomesticFlight;

    if (condition1) {
      // NH運航国際線. のツールチップ 	tooltip.terminal1
      return 'tooltip.terminal1';
    }
    if (condition2) {
      // スターフライヤー運航国内線 	tooltip.starflyerTerminal
      return 'tooltip.starflyerTerminal';
    }

    return '';
  }

  override reload(): void {
    // Add comment to passed SonarLint
  }

  override destroy(): void {
    if (this._subscriptions) this._subscriptions.unsubscribe();
  }
}
